import React, { useState } from 'react';
import { Search, Loader, FilterX, List, Table, Plus, Pencil, Save, X, Trash2 } from 'lucide-react';
import ActionMenu from './ActionMenu';
import DownloadModal from './DownloadModal';
import ColumnSettingsModal from './ColumnSettingsModal';

const ModuleHeader = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  searchPlaceholder,
  filters,
  clearAllFilters,
  filteredCount,
  totalCount,
  columns,
  filteredData,
  filename,
  // CRUD props for list view
  onAdd,
  onEdit,
  onSave,
  onCancel,
  canEdit = true,
  canSave = false,
  canCancel = false,
  isEditing = false,
  // Datasheet props
  editModeEnabled = false,
  onEditModeToggle,
  isDirty = false,
  onDatasheetSave,
  onDatasheetCancel,
  // Column settings props
  columnOrder,
  hiddenColumns,
  onColumnSettingsChange,
  // Permission
  userCanEdit = true,
  // Delete mode props
  deleteModeEnabled = false,
  selectedDeleteCount = 0,
  onEnterDeleteMode,
  onCancelDeleteMode,
  onConfirmDelete,
  onSearchSubmit,
  searchLoading = false
}) => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const hasActiveFilters = Object.values(filters).some(f => f?.length > 0);

  return (
    <div className="module-header">
      <div className="module-header-left">
        <div className="module-header-tabs">
          <button
            onClick={() => setViewMode('list')}
            className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
            title="List View"
          >
            <List size={18} />
          </button>
          <button
            onClick={() => setViewMode('datasheet')}
            className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`}
            title="Datasheet View"
          >
            <Table size={18} />
          </button>
        </div>
      </div>

      <div className="module-header-actions">
        {/* CRUD Buttons - List View Only, hidden for Guest users */}
        {viewMode === 'list' && userCanEdit && (
          <div className="module-header-crud">
            {onAdd && (
              <button
                onClick={onAdd}
                className={`crud-btn ${isEditing ? 'disabled' : ''}`}
                title="Add New Record"
                disabled={isEditing}
              >
                <Plus size={18} />
              </button>
            )}
            {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
            <button
              onClick={isEditing ? onCancel : onEdit}
              className={`crud-btn ${!canEdit && !isEditing ? 'disabled' : ''}`}
              title={isEditing ? 'Cancel' : 'Edit Record'}
              disabled={!canEdit && !isEditing}
            >
              {isEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
            <button
              onClick={onSave}
              className={`crud-btn ${!canSave ? 'disabled' : ''}`}
              title="Save Changes"
              disabled={!canSave}
            >
              <Save size={18} />
            </button>
          </div>
        )}

        {/* Datasheet Controls - Datasheet View Only */}
        {viewMode === 'datasheet' && (
          <div className="module-header-datasheet-controls">
            {/* Clear Filters */}
            <div className="datasheet-filter-clear">
              {hasActiveFilters && (
                <button onClick={clearAllFilters} title="Clear all filters" className="clear-filter-btn">
                  <FilterX size={16} />
                  <span>{filteredCount}/{totalCount}</span>
                </button>
              )}
            </div>
            {/* Delete Mode Controls - replaces Edit/Save when in delete mode */}
            {deleteModeEnabled && userCanEdit && (
              <div className="module-header-crud">
                <button
                  onClick={onCancelDeleteMode}
                  className="crud-btn"
                  title="Cancel Delete Mode"
                >
                  <X size={18} />
                </button>
                <button
                  onClick={onConfirmDelete}
                  className={`crud-btn delete-btn ${selectedDeleteCount === 0 ? 'disabled' : ''}`}
                  title={selectedDeleteCount > 0 ? `Delete ${selectedDeleteCount} selected` : 'Select rows to delete'}
                  disabled={selectedDeleteCount === 0}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
            {/* Edit/Save - hidden for Guest users, hidden in delete mode */}
            {!deleteModeEnabled && userCanEdit && onEditModeToggle && (
              <div className="module-header-crud">
                {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
                <button
                  onClick={onEditModeToggle}
                  className="crud-btn"
                  title={editModeEnabled ? 'Cancel' : 'Enable Editing'}
                >
                  {editModeEnabled ? <X size={18} /> : <Pencil size={18} />}
                </button>
                <button
                  onClick={onDatasheetSave}
                  className={`crud-btn ${!isDirty ? 'disabled' : ''}`}
                  title="Save Changes"
                  disabled={!isDirty}
                >
                  <Save size={18} />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="search-wrapper">
          {searchLoading
            ? <Loader size={16} className="search-icon spinning" />
            : <Search size={16} className="search-icon" />
          }
          <input
            type="text"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              if (!e.target.value) onSearchSubmit?.('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchSubmit?.(e.target.value);
              }
            }}
            placeholder={searchPlaceholder}
            className="search-input"
          />
        </div>
        {hasActiveFilters && viewMode !== 'datasheet' && (
          <button onClick={clearAllFilters} title="Clear all filters" className="clear-filter-btn">
            <FilterX size={16} />
            <span>{filteredCount}/{totalCount}</span>
          </button>
        )}
        <ActionMenu
          disabled={viewMode === 'list'}
          onDownload={() => setShowDownloadModal(true)}
          onModifyView={() => setShowColumnSettings(true)}
          showModifyView={viewMode === 'datasheet'}
          onBulkDelete={onEnterDeleteMode}
          showBulkDelete={viewMode === 'datasheet' && userCanEdit && !deleteModeEnabled && !editModeEnabled}
        />
      </div>
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        data={filteredData}
        columns={columns}
        tableName={filename}
      />
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        onApply={onColumnSettingsChange}
      />
    </div>
  );
};

export default ModuleHeader;
